import { Box, Collapse, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { vyDate } from '@vy/utils';
import type { MutableRefObject } from 'react';
import { useMemo, useState } from 'react';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useGridColumnSettings } from '../../../../hooks/use-grid-column-settings';
import { useRecurringRevenueReport } from '../../../../hooks/requests/use-recurring-revenue-report';
import { useFiltersParam } from '../../../../hooks/search-params/use-filters-param';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { ReportTable } from '../../reports-components/ReportTable';
import { RetentionTrendChart } from '../../reports-components/RetentionTrendChart/RetentionTrendChart';
import { selectActiveRowsInRange } from '../../reports-components/shared-filter-fields';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { NrrGrrReportTable } from '../components/NrrGrrReportTable';
import { NrrGrrReportToolbar } from '../components/NrrGrrReportToolbar';
import { useNrrGrrReportColumns } from '../hooks/use-nrr-grr-report-columns';
import { useNrrGrrReportData } from '../hooks/use-nrr-grr-report-data';
import { useNrrGrrReportFilterFields } from '../hooks/use-nrr-grr-report-filter-fields';

const FILTERS_PARAM_NAME = 'nrrGrrFilters';

export const NrrGrrReportContainer: React.FC = () => {
  const apiRef = useGridApiRef();

  const defaultFromDate = useMemo(
    () => vyDate().subtract(11, 'month').startOf('month').toDate(),
    [],
  );
  const defaultToDate = useMemo(() => vyDate().endOf('month').toDate(), []);

  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);
  const [showChart, setShowChart] = useState(false);

  const { data: recurringRevenueReportData } = useRecurringRevenueReport();
  const activeRowsForFilterOptions = useMemo(
    () => selectActiveRowsInRange(recurringRevenueReportData.report, fromDate, toDate),
    [recurringRevenueReportData.report, fromDate, toDate],
  );
  const filterFields = useNrrGrrReportFilterFields(activeRowsForFilterOptions);
  const [rawFilters, setFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = useMemo(() => rawFilters ?? [], [rawFilters]);

  const { rows, isLoading, creationDate } = useNrrGrrReportData(fromDate, toDate, filters);
  const columns = useNrrGrrReportColumns();

  const { openSettings, closeSettings, columnSettings, handleColumnVisibilityChange } =
    useGridColumnSettings(apiRef as MutableRefObject<GridApiCommunity>, columns);

  const handleExport = async () => {
    const api = apiRef.current;
    if (!api) return;

    await toastFromPromise(
      api.exportDataAsExcel({ fileName: 'NRR GRR Report' }),
      {
        pending: 'Downloading...',
        success: 'Download complete',
        error: 'Failed to download',
      },
    );
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          fontSize: "20px",
          mb: 1
        }}>
        NRR / GRR Report
      </Typography>
      <ReportTable
        title="NRR / GRR Report"
        hideTitle
        exportCsvFileName="NRR GRR Report"
        reportCreationDate={creationDate}
        reportType={REPORT_TYPES.NRR_GRR}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        hideMenu
      >
        <NrrGrrReportToolbar
          fromDate={fromDate}
          onFromDateChange={setFromDate}
          toDate={toDate}
          onToDateChange={setToDate}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={setFilters}
          onColumnSettingsOpen={openSettings}
          onColumnSettingsClose={closeSettings}
          columnSettings={columnSettings}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onExport={handleExport}
          showChart={showChart}
          onShowChartChange={setShowChart}
        />

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Collapse in={showChart} timeout={350} unmountOnExit>
            <RetentionTrendChart fromDate={fromDate} toDate={toDate} filters={filters} />
          </Collapse>

          <Box
            sx={(theme) => ({
              height: showChart ? `calc(100vh - ${theme.spacing(30)})` : '100%',
              minHeight: 500,
            })}
          >
            <NrrGrrReportTable rows={rows} columns={columns} isLoading={isLoading} apiRef={apiRef as MutableRefObject<GridApiPremium>} />
          </Box>
        </Box>
      </ReportTable>
    </>
  );
};
